"use client";

import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";

type Props = {
  images: string[];
  alts?: string[];
  current: number;
  onSelect: (index: number) => void;
  pageSize?: number;
  className?: string;
};

export default function VerticalThumbScroller({
  images,
  alts,
  current,
  onSelect,
  pageSize = 7,
  className = "",
}: Props) {
  const [page, setPage] = useState(0);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(images.length / pageSize));
  }, [images.length, pageSize]);

  const clampPage = useCallback(
    (p: number) => Math.min(Math.max(p, 0), totalPages - 1),
    [totalPages]
  );

  useEffect(() => {
    if (!images.length) return;

    const targetPage = Math.floor(current / pageSize);

    setPage((prevPage) => {
      const nextPage = clampPage(targetPage);
      return prevPage === nextPage ? prevPage : nextPage;
    });
  }, [current, images.length, pageSize, clampPage]);

  const pageStart = page * pageSize;

  const visibleImages = useMemo(() => {
    return images.slice(pageStart, pageStart + pageSize);
  }, [images, pageStart, pageSize]);

  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  const goPrev = useCallback(() => {
    setPage((prevPage) => clampPage(prevPage - 1));
  }, [clampPage]);

  const goNext = useCallback(() => {
    setPage((prevPage) => clampPage(prevPage + 1));
  }, [clampPage]);

  const goToPage = useCallback(
    (pageIndex: number) => {
      const nextPage = clampPage(pageIndex);
      const targetIndex = Math.min(nextPage * pageSize, images.length - 1);

      setPage(nextPage);

      if (targetIndex >= 0) {
        onSelect(targetIndex);
      }
    },
    [clampPage, images.length, onSelect, pageSize]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        goPrev();
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        goNext();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext]);

  if (!images.length) return null;

  return (
    <div
      className={[
        "relative z-[5] flex flex-col items-stretch",
        "w-full md:w-auto",
        className,
      ].join(" ")}
      aria-label="Vertical thumbnail scroller"
    >
      {/* Top Arrow */}
      <button
        type="button"
        onClick={goPrev}
        disabled={!canPrev}
        aria-label="Previous thumbnails"
        className={[
          "absolute z-10 flex items-center justify-center",
          "h-8 w-8 rounded-full",
          "bg-black/60 text-white",
          "transition-all duration-300",
          "md:left-1/2 md:top-[-38px] md:-translate-x-1/2",
          "max-md:left-[30px] max-md:bottom-[40px] max-md:rotate-[270deg]",
          canPrev
            ? "opacity-100 hover:bg-black cursor-pointer"
            : "opacity-40 cursor-not-allowed",
        ].join(" ")}
      >
        ↑
      </button>

      {/* Thumbnail Box */}
      <div
        className={[
          "relative overflow-hidden",
          "rounded-xl",
          "bg-transparent md:bg-[#0606068c]",
          "px-4 py-4",
          "md:px-6 md:py-6",
          "xl:px-8",
          "max-md:w-full",
        ].join(" ")}
      >
        <div
          className={[
            "grid gap-3",
            "grid-cols-4",
            "md:grid-cols-1",
            "md:gap-[15px]",
          ].join(" ")}
        >
          {visibleImages.map((img, i) => {
            const globalIndex = pageStart + i;
            const isActive = current === globalIndex;

            return (
              <button
                key={`${img}-${globalIndex}`}
                type="button"
                onClick={() => onSelect(globalIndex)}
                aria-label={`Open gallery image ${globalIndex + 1}`}
                aria-current={isActive ? "true" : undefined}
                className={[
                  "relative cursor-pointer overflow-hidden",
                  "w-full h-[64px]",
                  "sm:w-24 sm:h-16",
                  "md:w-[130px] md:h-[80px]",
                  "border-[3px]",
                  "transition-all duration-300",
                  "focus:outline-none focus:ring-2 focus:ring-white/70",
                  isActive
                    ? "border-white opacity-100"
                    : "border-transparent opacity-80 hover:opacity-100 hover:border-white/60",
                ].join(" ")}
              >
                <Image
                  src={img}
                  alt={alts?.[globalIndex] || `Thumbnail ${globalIndex + 1}`}
                  fill
                  sizes="(max-width: 768px) 25vw, 130px"
                  loading={globalIndex <= 2 ? "eager" : "lazy"}
                  className="object-cover transition-transform duration-300 hover:scale-110"
                  draggable={false}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom Arrow */}
      <button
        type="button"
        onClick={goNext}
        disabled={!canNext}
        aria-label="Next thumbnails"
        className={[
          "absolute z-10 flex items-center justify-center",
          "h-8 w-8 rounded-full",
          "bg-black/60 text-white",
          "transition-all duration-300",
          "md:left-1/2 md:bottom-[-38px] md:-translate-x-1/2",
          "max-md:right-[30px] max-md:bottom-[40px] max-md:rotate-[270deg]",
          canNext
            ? "opacity-100 hover:bg-black cursor-pointer"
            : "opacity-40 cursor-not-allowed",
        ].join(" ")}
      >
        ↓
      </button>

      {/* Page Dots */}
      <div className="mt-2 flex items-center justify-center gap-3 max-md:pb-[15px]">
        {Array.from({ length: totalPages }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goToPage(i)}
            aria-label={`Go to thumbnail page ${i + 1}`}
            className={[
              "inline-block h-2 w-6 rounded-full transition-all duration-300",
              i === page
                ? "bg-white max-md:bg-[#000]/60"
                : "bg-white/30 max-md:bg-[#000]/20",
            ].join(" ")}
          />
        ))}
      </div>
    </div>
  );
}